// AI管理 · OCR安定化設定と診断 / OCR stability settings & diagnostics
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowsClockwise } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { jstDateTime } from '@/utils/jstDate';

const API = process.env.REACT_APP_BACKEND_URL;
const FIELDS = [
  { key: 'vision_concurrency', ja: '画像リクエストの同時実行（モデル単位）',
    en: 'Local vision concurrency (per model)', min: 1, max: 4 },
  { key: 'page_concurrency', ja: '同時に処理するページ数',
    en: 'Concurrent pages', min: 1, max: 4 },
  { key: 'lease_timeout_seconds', ja: '順番待ちの上限（秒）',
    en: 'Queue acquisition timeout (s)', min: 10, max: 900 },
  { key: 'max_image_longest_side', ja: '画像の長辺の上限（px）',
    en: 'Max image longest side (px)', min: 800, max: 4096 },
  { key: 'max_retries', ja: '再試行の上限', en: 'Retry limit', min: 0, max: 4 },
];

// 書類の取り込みキュー（ドキュメント設定と同じ値を編集する）
const QUEUE_FIELDS = [
  { key: 'ingest_max_concurrent', ja: '同時に読み取る件数（全体）',
    en: 'Ingest max concurrent', min: 1, max: 32 },
  { key: 'ingest_per_user_limit', ja: '1人あたりの同時実行',
    en: 'Per-user limit', min: 1, max: 32 },
  { key: 'ingest_queue_timeout_min', ja: '順番待ちの上限（分）',
    en: 'Queue timeout (min)', min: 5, max: 1440 },
];

const Pill = ({ tone = 'zinc', children, testId }) => (
  <span data-testid={testId}
    className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] ${{
      teal: 'border-teal-200 bg-teal-50 text-teal-800 dark:border-teal-800 dark:bg-teal-950 dark:text-teal-200',
      amber: 'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200',
      rose: 'border-rose-200 bg-rose-50 text-rose-800 dark:border-rose-800 dark:bg-rose-950 dark:text-rose-200',
      zinc: 'border-zinc-200 bg-zinc-50 text-zinc-700 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-300',
    }[tone]}`}>{children}</span>
);

const Card = ({ title, subtitle, action, children, testid }) => (
  <section data-testid={testid}
    className="rounded-lg border border-zinc-200 bg-white p-4 dark:border-zinc-700 dark:bg-zinc-900">
    <div className="flex flex-wrap items-start justify-between gap-2 pb-2">
      <div>
        <h3 className="text-base font-medium text-zinc-900 dark:text-zinc-100">{title}</h3>
        {subtitle && <p className="text-[11.5px] text-zinc-500 dark:text-zinc-400">{subtitle}</p>}
      </div>
      {action}
    </div>
    {children}
  </section>
);

export default function StabilitySection() {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({});
  const [diag, setDiag] = useState(null);
  const [queue, setQueue] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [c, a, q] = await Promise.all([
        axios.get(`${API}/api/doc-ai/config`),
        axios.get(`${API}/api/ai/console/analytics`, { params: { days: 7 } }),
        axios.get(`${API}/api/dms/settings`).catch(() => null),
      ]);
      setCfg(c.data.config);
      setForm(c.data.config);
      setDiag(a.data);
      if (q) setQueue(q.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || '設定を取得できませんでした');
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy(true);
    try {
      const body = {};
      FIELDS.forEach((f) => { body[f.key] = Number(form[f.key]); });
      const r = await axios.put(`${API}/api/doc-ai/config`, body);
      setCfg(r.data.config);
      toast.success('OCR安定化の設定を保存しました');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '保存できませんでした');
    } finally { setBusy(false); }
  };

  const saveQueue = async () => {
    setBusy(true);
    try {
      const r = await axios.put(`${API}/api/dms/settings`, {
        trash_retention_days: Number(queue.trash_retention_days ?? 90),
        ingest_max_concurrent: Number(queue.ingest_max_concurrent),
        ingest_per_user_limit: Number(queue.ingest_per_user_limit),
        ingest_queue_timeout_min: Number(queue.ingest_queue_timeout_min),
      });
      setQueue(r.data);
      toast.success('取り込みキューの設定を保存しました');
    } catch (e) {
      toast.error(e?.response?.data?.detail || '保存できませんでした');
    } finally { setBusy(false); }
  };

  if (!cfg) return null;
  const errors = diag?.errors;
  return (
    <div className="space-y-4" data-testid="ai-stability-section">
      <Card testid="ocr-stability-settings" title="OCR安定化設定 / OCR stability"
        subtitle="ローカルの画像リクエストの圧力を抑えます。まずは同時実行1で安定を確認し、1項目ずつ増やしてください。"
        action={(
          <Button className="h-10 bg-teal-700 text-[12px] hover:bg-teal-800" onClick={save}
            disabled={busy} data-testid="ocr-stability-save">保存 / Save</Button>
        )}>
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {FIELDS.map((f) => (
            <label key={f.key} className="block" htmlFor={`stab-${f.key}`}>
              <span className="block text-[11.5px] text-zinc-700 dark:text-zinc-300">
                {f.ja}
              </span>
              <span className="block text-[10.5px] text-zinc-400">{f.en} ({f.min}–{f.max})</span>
              <input id={`stab-${f.key}`} type="number" min={f.min} max={f.max}
                value={form[f.key] ?? ''}
                onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                className="mt-1 h-10 w-full rounded-md border border-zinc-200 px-2 text-[12.5px] dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-100"
                data-testid={`ocr-stability-${f.key}`} />
            </label>
          ))}
        </div>
        <p className="pt-2 text-[11px] text-zinc-500 dark:text-zinc-400">
          この設定は当アプリの要求を制御するものです。LM Studio 側の Auto-Evict や
          Idle TTL、同時予測数は変更しません（サーバー側の運用設定です）。/ These limits
          govern this application&apos;s own requests. LM Studio Auto-Evict, idle TTL and
          server-side concurrency are not changed by this app.
        </p>
      </Card>

      {queue && (
        <Card testid="ingest-queue-settings" title="書類の取り込みキュー / Document ingest queue"
          subtitle="一括取り込みの同時実行と順番待ちの上限。大量に入れるときだけ上げてください（既定: 全体2件・1人2件・120分）。"
          action={(
            <Button className="h-10 bg-teal-700 text-[12px] hover:bg-teal-800" onClick={saveQueue}
              disabled={busy} data-testid="ingest-queue-save">保存 / Save</Button>
          )}>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {QUEUE_FIELDS.map((f) => (
              <label key={f.key} className="block" htmlFor={`queue-${f.key}`}>
                <span className="block text-[11.5px] text-zinc-700 dark:text-zinc-300">{f.ja}</span>
                <span className="block text-[10.5px] text-zinc-400">{f.en} ({f.min}–{f.max})</span>
                <input id={`queue-${f.key}`} type="number" min={f.min} max={f.max}
                  value={queue[f.key] ?? ''}
                  onChange={(e) => setQueue({ ...queue, [f.key]: e.target.value })}
                  className="mt-1 h-10 w-full rounded-md border border-zinc-200 px-2 text-[12.5px] dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-100"
                  data-testid={`ingest-queue-${f.key}`} />
              </label>
            ))}
          </div>
          <p className="pt-2 text-[11px] text-zinc-500 dark:text-zinc-400">
            順番待ちの時間は「順番が回ってくるまで」は数えません。上限を超えても一度は自動で並び直してから失敗にします。
          </p>
        </Card>
      )}

      <Card testid="ai-diagnostics" title="失敗の内訳と診断 / Failure breakdown"
        subtitle="直近7日。観測できた事実のみを表示します（推測は possible_* として区別）。"
        action={(
          <Button variant="outline" className="h-10 text-[12px]" onClick={load}
            data-testid="ai-diagnostics-refresh">
            <ArrowsClockwise size={13} className="mr-1" />更新 / Refresh
          </Button>
        )}>
        <div className="flex flex-wrap gap-1.5" data-testid="ai-diagnostics-summary">
          <Pill>要求 {diag?.total_requests ?? 0}件</Pill>
          <Pill tone={errors?.total ? 'amber' : 'teal'}>失敗 {errors?.total ?? 0}件</Pill>
          <Pill tone={errors?.model_unload_observed ? 'amber' : 'zinc'}>
            解放・入れ替えを観測 {errors?.model_unload_observed ?? 0}
          </Pill>
          <Pill tone={errors?.runtime_crash ? 'rose' : 'zinc'}>
            実行環境のクラッシュ {errors?.runtime_crash ?? 0}
          </Pill>
          <Pill tone={errors?.out_of_memory ? 'rose' : 'zinc'}>
            メモリ不足 {errors?.out_of_memory ?? 0}
          </Pill>
          <Pill>予備モデル 試行 {errors?.fallback_attempted ?? 0} / 使用 {errors?.fallback_used ?? 0}
            {' '}/ 拒否 {errors?.fallback_rejected ?? 0}</Pill>
          <Pill tone={errors?.concurrency_waits ? 'amber' : 'zinc'}>
            順番待ち {errors?.concurrency_waits ?? 0}件（最大 {errors?.concurrency_wait_ms_max ?? 0}ms）
          </Pill>
          {(diag?.languages || []).map((l) => (
            <Pill key={l.language}>回答言語 {l.language}: {l.count}</Pill>
          ))}
        </div>

        <div className="overflow-x-auto pt-3">
          <table className="w-full min-w-[520px] text-[11.5px]" data-testid="ai-error-types">
            <thead>
              <tr className="text-left text-zinc-500 dark:text-zinc-400">
                <th className="py-1 font-medium">種類 / Type</th>
                <th className="py-1 font-medium">件数</th>
                <th className="py-1 font-medium">機能</th>
                <th className="py-1 font-medium">モデル</th>
              </tr>
            </thead>
            <tbody>
              {(errors?.by_type || []).map((e) => (
                <tr key={e.error_type} className="border-t border-zinc-100 dark:border-zinc-800"
                  data-testid={`ai-error-type-${e.error_type}`}>
                  <td className="py-1 text-zinc-800 dark:text-zinc-200">
                    {e.label_ja} <span className="text-zinc-400">/ {e.label_en}</span>
                  </td>
                  <td className="py-1 tabular-nums text-zinc-800 dark:text-zinc-200">{e.count}</td>
                  <td className="py-1 text-zinc-600 dark:text-zinc-400">
                    {Object.entries(e.features || {}).map(([k, v]) => `${k}:${v}`).join(' / ')}
                  </td>
                  <td className="py-1 text-zinc-600 dark:text-zinc-400">
                    {Object.entries(e.models || {}).map(([k, v]) => `${k}:${v}`).join(' / ')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <details className="pt-3" data-testid="ai-error-recent">
          <summary className="cursor-pointer text-[12px] text-teal-700 dark:text-teal-300">
            直近の失敗（詳細） / Recent failures
          </summary>
          <div className="space-y-1 pt-2">
            {(errors?.recent || []).map((r, i) => (
              <div key={`${r.ts}-${i}`}
                className="rounded-md border border-zinc-200 px-2 py-1.5 text-[11px] dark:border-zinc-700"
                data-testid={`ai-error-recent-${i}`}>
                <p className="text-zinc-800 dark:text-zinc-200">
                  {jstDateTime(r.ts, true)} · {r.feature} · {r.model}
                  {' · '}{r.error_type}{r.error_stage ? ` (${r.error_stage})` : ''}
                  {r.provider_http_status ? ` · HTTP ${r.provider_http_status}` : ''}
                </p>
                <p className="text-zinc-500 dark:text-zinc-400">
                  {r.provider_error_summary_sanitised || '—'}
                </p>
                <p className="text-zinc-400">
                  モデル状態 {r.model_state_before || '—'} → {r.model_state_after || '—'}
                  {r.model_state_stale ? '（情報が古い可能性）' : ''}
                  {r.image_count ? ` · 画像 ${r.image_count}枚` : ''}
                  {r.concurrency_wait_ms ? ` · 順番待ち ${r.concurrency_wait_ms}ms` : ''}
                  {r.request_id ? ` · req ${r.request_id}` : ''}
                  {r.document_read_audit_id ? ` · read ${r.document_read_audit_id}` : ''}
                </p>
              </div>
            ))}
          </div>
        </details>
        <p className="pt-2 text-[11px] text-zinc-500 dark:text-zinc-400">
          {errors?.notice_ja} / {errors?.notice_en}
        </p>
        <p className="pt-1 text-[11px] text-zinc-400">
          同時実行の状態: {(diag?.vision_governor?.keys || []).length}件のモデル
          {diag?.vision_governor?.process_local
            ? '（この制御はバックエンドのプロセス単位です）'
            : ''}
        </p>
      </Card>
    </div>
  );
}
